import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Reveal from "@/components/Reveal";
import ProjectCard from "@/components/ProjectCard";
import { fetchProjects } from "@/lib/api";
import { usePageMeta } from "@/lib/meta";
import { BUDGET_BANDS, CITIES, PROPERTY_TYPES } from "@/lib/site";

export default function Portfolio() {
  usePageMeta(
    "Portfolio — Render vs. Reality | Shriya Redkar Interiors",
    "Every project shown as the approved 3D render beside the finished site photo. Filter by city, budget band and property type."
  );
  const [city, setCity] = useState<string>("all");
  const [budget, setBudget] = useState<string>("all");
  const [type, setType] = useState<string>("all");

  const { data: projects, isLoading } = useQuery({ queryKey: ["projects"], queryFn: fetchProjects });

  const filtered = useMemo(() => {
    if (!projects) return [];
    return projects.filter(
      (p) =>
        (city === "all" || p.city === city) &&
        (budget === "all" || p.budget_band === budget) &&
        (type === "all" || p.property_type === type)
    );
  }, [projects, city, budget, type]);

  const chip = (active: boolean, testid: string, label: string, onClick: () => void) => (
    <button
      key={testid}
      data-testid={testid}
      onClick={onClick}
      className={`border px-4 py-2 font-display text-[11px] font-medium uppercase tracking-[0.14em] transition-all duration-300 ${
        active
          ? "border-gold bg-gold text-obsidian"
          : "border-white/15 text-zinc-400 hover:border-gold/60 hover:text-gold"
      }`}
    >
      {label}
    </button>
  );

  return (
    <main className="pt-[76px]">
      <section className="container-x py-16 lg:py-24">
        <Reveal>
          <p className="eyebrow">Portfolio</p>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-light leading-[1.08] text-zinc-50 sm:text-5xl lg:text-6xl">
            Every project, <span className="italic text-gold">both</span> pictures.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400">
            The approved 3D and the finished photo — every time, no exceptions. Filter to your city and
            budget to see what we've delivered near you.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div className="space-y-4 border border-white/10 bg-obsidian-soft p-6" data-testid="portfolio-filters">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-2 w-24 font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">City</span>
              {chip(city === "all", "filter-city-all", "All cities", () => setCity("all"))}
              {CITIES.map((c) => chip(city === c.name, `filter-city-${c.slug}`, c.name, () => setCity(c.name)))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-2 w-24 font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">Budget</span>
              {chip(budget === "all", "filter-budget-all", "Any", () => setBudget("all"))}
              {BUDGET_BANDS.map((b) =>
                chip(budget === b, `filter-budget-${b.replace(/[₹–+]/g, "")}`, b, () => setBudget(b))
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-2 w-24 font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">Property</span>
              {chip(type === "all", "filter-type-all", "All", () => setType("all"))}
              {PROPERTY_TYPES.map((t) => chip(type === t, `filter-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`, t, () => setType(t)))}
            </div>
          </div>
        </Reveal>

        <div className="mt-8 flex items-center justify-between">
          <p className="font-mono text-xs text-zinc-500" data-testid="portfolio-count">
            {isLoading ? "Loading projects…" : `${filtered.length} project${filtered.length === 1 ? "" : "s"}`}
          </p>
        </div>

        <div className="mt-6 grid gap-6 md:grid-cols-2" data-testid="portfolio-grid">
          {filtered.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 2) * 0.08}>
              <div className="aspect-[4/3] w-full">
                <ProjectCard project={p} />
              </div>
            </Reveal>
          ))}
        </div>

        {!isLoading && filtered.length === 0 && (
          <div className="mt-10 border border-white/10 bg-obsidian-soft p-12 text-center" data-testid="portfolio-empty-state">
            <p className="font-serif text-2xl text-zinc-300">No projects match that combination — yet.</p>
            <p className="mt-2 text-sm text-zinc-500">Try widening the filters, or ask us about similar work on a call.</p>
            <button
              onClick={() => {
                setCity("all");
                setBudget("all");
                setType("all");
              }}
              data-testid="portfolio-filters-reset"
              className="btn-ghost mt-6"
            >
              Reset filters
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
